<template>
  <b-table
    id="table-clickable"
    ref="invoiceList"
    :hover="hover"
    responsive="sm"
    :items="invoicesItems"
    :fields="invoicesFields"
    :current-page="currentPage"
    :per-page="0"
    bordered
    small
    sticky-header="700px"
    :head-variant="hv"
    :busy="isBusy"
    tbody-tr-class="tr-cursor"
    :empty-text="FormMSG(1, 'No invoice found')"
    show-empty
    @row-clicked="(item) => $emit('row-clicked', item)"
  >
    <template #table-busy>
      <div class="d-flex justify-content-center align-items-center">
        <b-spinner class="align-middle" small />
        <strong class="ml-2">{{ FormMSG(2, 'Loading') }}...</strong>
      </div>
    </template>
    <template v-if="!forApprove && !forPending && !forApproved" #head(select)>
      <div id="select-all" class="text-center vertical-centered">
        <b-form-checkbox v-model="selectAll" size="lg" class="cb-border-white" @change="$emit('select-all')" />
      </div>
    </template>
    <template v-if="!forApprove && !forApproved" #cell(select)="{ item }">
      <span v-if="!forPending || item.subType === INVOICE_SUBTYPE.ELEC_INVOICE_TO_RECEIVE">
        <b-form-checkbox v-model="item.selectState" size="lg" class="pj-cb pb-1" @change="$emit('select', item)" />
      </span>
    </template>
    <template #cell(supplierName)="{ item }">
      <div class="d-flex align-items-center" :class="{ 'justify-content-between': item.documents && item.documents.length <= 0 }" style="gap: 7px">
        <span>{{ item.supplierName }}</span>
        <div v-if="item.documents && item.documents.length <= 0" class="mx-2 p-1 border warning-invoice rounded d-flex align-items-center" style="gap: 7px">
          <component :is="getLucideIcon('AlertCircle')" :size="16" :stroke-width="1.5" />
          <span class="small">{{ FormMSG(3, 'no invoice attached') }}</span>
        </div>
      </div>
    </template>
    <template #cell(po)="{ item }">
      <div class="wrap-status">
        <div class="status" :class="provideSubTypeClass(item.subType)" style="font-size: 0.7rem">
          {{ provideSubTypeText(item.subType, 44, 45, 46) }}
        </div>
      </div>
      <!-- We replaced the following line because PO is not in use yet with invoices. But we may restore it in a needed new features later -->
      <!-- <template v-if="[INVOICE_SUBTYPE.ELEC_INVOICE_TO_RECEIVE, INVOICE_SUBTYPE.ELEC_INVOICE].includes(item.subType)">
        <div class="wrap-status">
          <div
            class="status"
            :class="item.subType === INVOICE_SUBTYPE.ELEC_INVOICE ? 'bg-gray-700 text-light' : 'bg-gray-200 text-dark'"
            style="font-size: 0.7rem"
          >
            {{ item.subType === INVOICE_SUBTYPE.ELEC_INVOICE ? FormMSG(44, 'Electronic Invoice') : FormMSG(45, 'Electronic Invoice To Receive') }}
          </div>
        </div>
      </template>
      <template v-else-if="item.purchaseOrders && item.purchaseOrders.length > 0">
        <div v-for="po in item.purchaseOrders" :key="po.id">
          <span class="py-2">{{ po.id }}</span>
        </div>
      </template>
      <template v-else>
        <div>
          <span>{{ FormMSG(4, 'No PO') }}</span>
        </div>
      </template> -->
    </template>
    <template #cell(validatedStatus)="data">
      <div class="d-flex align-items-center justify-content-center flex-row w-100" style="gap: 5px">
        <div v-if="data.item.allExpenseItemIsInContract === false" class="d-flex justify-content-end" style="width: 40%">
          <button
            v-b-tooltip.left.html
            class="btn-transparent text-color-rhapsody-in-blue"
            :title="FormMSG(5, 'This invoice is outside of the user\'s contract dates. You can still validate it')"
          >
            <component :is="getLucideIcon('AlertTriangle')" color="#EA4E" :size="18" />
          </button>
        </div>
        <!-- <div v-else class="d-flex justify-content-end" style="width: 50%"></div> -->
        <div class="d-flex align-items-center justify-content-center wrap-status">
          <div :id="`popover-refused-${data.item.id}`" :class="`status  ${data.item.statusClass}`" style="font-size: 0.7rem">
            {{ data.item.validatedStatus }}
          </div>
          <b-popover v-if="isInvoiceRefused(data.item)" :target="`popover-refused-${data.item.id}`" boundary="window" triggers="hover">
            {{ data.item.comment }}
          </b-popover>
        </div>
      </div>
    </template>
    <template #cell(description)="data">
      <span class="text-description-overflow-ellypsis">
        {{ data.item.description }}
      </span>
    </template>
  </b-table>
</template>

<script>
// props
import mapProps from '@/shared/vuePropsMapper';
// mixin
import globalMixin from '@/mixins/global.mixin';
import languageMessages from '@/mixins/languageMessages';
import invoicesToolsMixin from '@/mixins/invoicesTools.mixin';
import { INVOICE_SUBTYPE } from '../../../shared/constants';

export default {
  name: 'InvoicesDatatable',
  mixins: [languageMessages, globalMixin, invoicesToolsMixin],
  props: {
    ...mapProps(['forApprove', 'forDraft', 'forPending', 'forApproved', 'forMultipleInvoice', 'forEfcDraft', 'forEfcSubmitted'], {
      type: Boolean,
      default: false
    }),
    ...mapProps(['hover', 'striped', 'bordered'], {
      type: Boolean,
      default: true
    }),
    isSelectedAll: {
      type: Boolean,
      default: false
    },
    isBusy: {
      type: Boolean,
      default: false
    },
    canSeeCrewMember: {
      type: Boolean,
      default: false
    },
    invoicesItems: {
      type: Array,
      default: () => []
    },
    invoicesFields: {
      type: Array,
      default: () => [],
      required: true
    }
  },
  data() {
    return {
      filter: '',
      currentPage: 1,
      hv: 'dark',
      selectAll: true,
      INVOICE_SUBTYPE
    };
  },
  watch: {
    isSelectedAll: {
      handler(newVal) {
        this.selectAll = newVal;
      },
      immediate: true
    }
  }
};
</script>
